import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { baseAPI } from '../../../config';

const AIAgentStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${baseAPI}/ai-agent/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching AI agent status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (loading) {
    return <div className='text-sm text-slate-500'>Cargando estado...</div>;
  }

  if (!status) {
    return <div className='text-sm text-slate-500'>No se pudo obtener el estado del agente</div>;
  }

  const stateConfig = {
    running: { label: 'Activo', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
    idle: { label: 'Inactivo', color: 'bg-slate-400', textColor: 'text-slate-700', bgColor: 'bg-slate-50' },
    disabled: { label: 'Deshabilitado', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' },
  };

  const stateInfo = stateConfig[status.scheduler_state] || stateConfig.disabled;

  const formatTimeAgo = timestamp => {
    if (!timestamp) return 'Nunca';
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Hace un momento';
    if (minutes < 60) return `Hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Hace ${hours}h`;
    return `Hace ${Math.floor(hours / 24)}d`;
  };

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
      {/* Scheduler state */}
      <div className={`rounded-lg border border-slate-200 p-4 ${stateInfo.bgColor}`}>
        <div className='text-xs font-semibold uppercase text-slate-500 mb-2'>Scheduler</div>
        <div className='flex items-center gap-2'>
          <span className={`w-2.5 h-2.5 rounded-full ${stateInfo.color}`}></span>
          <span className={`text-sm font-medium ${stateInfo.textColor}`}>{stateInfo.label}</span>
        </div>
      </div>

      {/* Queue */}
      <div className='rounded-lg border border-slate-200 p-4'>
        <div className='text-xs font-semibold uppercase text-slate-500 mb-2'>Cola</div>
        <div className='space-y-1'>
          <div className='flex justify-between text-sm'>
            <span className='text-slate-600'>Pendientes</span>
            <span className='font-medium text-yellow-600'>{status.queue?.pending_count || 0}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-slate-600'>En progreso</span>
            <span className='font-medium text-blue-600'>{status.queue?.in_progress_count || 0}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-slate-600'>Fallidos</span>
            <span className='font-medium text-red-600'>{status.queue?.failed_count || 0}</span>
          </div>
        </div>
      </div>

      {/* Today */}
      <div className='rounded-lg border border-slate-200 p-4'>
        <div className='text-xs font-semibold uppercase text-slate-500 mb-2'>Hoy</div>
        <div className='space-y-1'>
          <div className='flex justify-between text-sm'>
            <span className='text-slate-600'>Procesados</span>
            <span className='font-medium'>{status.today?.processed || 0}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-slate-600'>Completados</span>
            <span className='font-medium text-green-600'>{status.today?.completed || 0}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-slate-600'>Errores</span>
            <span className='font-medium text-red-600'>{status.today?.errors || 0}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-slate-600'>Coste estimado</span>
            <span className='font-medium'>${status.today?.estimated_cost_usd?.toFixed(2) || '0.00'}</span>
          </div>
        </div>
      </div>

      {/* Last batch */}
      <div className='rounded-lg border border-slate-200 p-4'>
        <div className='text-xs font-semibold uppercase text-slate-500 mb-2'>Ultimo batch</div>
        {status.last_batch ? (
          <div className='space-y-1'>
            <div className='text-xs text-slate-400'>{formatTimeAgo(status.last_batch.timestamp)}</div>
            <div className='flex justify-between text-sm'>
              <span className='text-slate-600'>Procesados</span>
              <span className='font-medium'>{status.last_batch.processed}</span>
            </div>
            <div className='flex justify-between text-sm'>
              <span className='text-slate-600'>Completados</span>
              <span className='font-medium text-green-600'>{status.last_batch.completed}</span>
            </div>
            <div className='flex justify-between text-sm'>
              <span className='text-slate-600'>Duracion</span>
              <span className='font-medium'>{(status.last_batch.duration_ms / 1000).toFixed(1)}s</span>
            </div>
          </div>
        ) : (
          <div className='text-sm text-slate-400'>Sin datos</div>
        )}
      </div>
    </div>
  );
};

export default AIAgentStatus;
